import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import { useLayoutEffect, useRef, useState } from "react";

export interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface Props {
  /** ハイライト対象の矩形。null の場合はハイライトなし（説明のみ） */
  rect: SpotlightRect | null;
  title: string;
  body: string;
  /** 「次へ」ボタンを表示するか（説明ステップは true、操作待ちステップは false） */
  showNext: boolean;
  /** 最後のステップなら「完了」、それ以外は「次へ」 */
  isLast: boolean;
  stepIndex: number;
  totalSteps: number;
  accent: string;
  isDark: boolean;
  onNext: () => void;
  /** 操作待ちステップで「スキップ」を押したとき（取るべき操作を代行して次へ進む） */
  onSkip: () => void;
  onExit: () => void;
}

const CARD_W = 320;
const PAD = 6;
/** カード既定位置（右下）の画面端からの余白 */
const MARGIN = 16;
/** ハイライトを回避するときに空ける間隔 */
const AVOID_GAP = 12;
/** ハイライトリング（枠 2px + 光彩 4px）を含めた外側余白 */
const RING = PAD + 8;

const clamp = (v: number, min: number, max: number): number =>
  Math.max(min, Math.min(max, v));

/**
 * 暗転オーバーレイ + スポットライト（ハイライト部分のみクリック透過）+ コーチマークカード。
 *
 * カードは視線移動を減らすため画面右下に固定表示する。ハイライトとかぶる場合のみ
 * 「横（ハイライトの左隣）→ 上 → 右上」の順で自動的に退避する。
 * ヘッダーをドラッグすると任意の位置へ移動でき、以降はその位置を維持する。
 *
 * コンテナ自体は pointer-events:none とし、暗転を構成する 4 枚の枠 div とカードにのみ
 * pointer-events:auto を付与する。これによりハイライト中央の「穴」のクリックは
 * 背後の実要素（サンドボックスのボタン等）にそのまま届く。
 */
export const SpotlightOverlay = ({
  rect,
  title,
  body,
  showNext,
  isLast,
  stepIndex,
  totalSteps,
  accent,
  isDark,
  onNext,
  onSkip,
  onExit,
}: Props) => {
  const dim = "rgba(0,0,0,0.62)";
  const cardBg = isDark ? "#1e1e1e" : "#ffffff";
  const textColor = isDark ? "#e0e0e0" : "#212121";
  const subColor = isDark ? "#9e9e9e" : "#757575";
  const borderColor = isDark ? "#333333" : "#e0e0e0";

  const vw = typeof window !== "undefined" ? window.innerWidth : 1280;
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;

  // ===== ドラッグ移動（ヘッダーをつかんでカードを動かす） =====
  const cardRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    baseTop: number;
    baseLeft: number;
  } | null>(null);
  // ユーザーがドラッグした場合の手動位置。一度ドラッグしたらステップが進んでも
  // その位置を維持する（「決めた場所に固定」を優先し、自動退避は行わない）。
  const [dragPos, setDragPos] = useState<{ top: number; left: number } | null>(
    null,
  );

  const handlePointerDown = (e: React.PointerEvent) => {
    // ヘッダー内のボタン（×）押下時はドラッグを開始しない
    if ((e.target as HTMLElement).closest("button")) return;
    const el = cardRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    dragRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      baseTop: r.top,
      baseLeft: r.left,
    };
    setDragPos({ top: r.top, left: r.left });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const d = dragRef.current;
    if (!d) return;
    const nextTop = d.baseTop + (e.clientY - d.startY);
    const nextLeft = d.baseLeft + (e.clientX - d.startX);
    // カードの一部が画面外に出すぎないよう緩く制限する
    setDragPos({
      top: clamp(nextTop, 0, vh - 40),
      left: clamp(nextLeft, -(CARD_W - 80), vw - 80),
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId))
      e.currentTarget.releasePointerCapture(e.pointerId);
  };

  // ===== コーチマークカードの位置（右下固定 + ハイライト回避） =====
  // null = 既定の右下。ハイライトとかぶるステップでのみ退避位置を持つ。
  const [autoPos, setAutoPos] = useState<{
    right: number;
    bottom: number;
  } | null>(null);

  useLayoutEffect(() => {
    if (dragPos) return; // 手動位置が優先
    const cardH = cardRef.current?.offsetHeight ?? 0;
    if (!rect || cardH === 0) {
      setAutoPos(null);
      return;
    }
    // ハイライトリングの外接矩形
    const hLeft = rect.left - RING;
    const hTop = rect.top - RING;
    const hRight = rect.left + rect.width + RING;
    const hBottom = rect.top + rect.height + RING;
    // 既定位置（右下）でのカード矩形
    const cLeft = vw - MARGIN - CARD_W;
    const cTop = vh - MARGIN - cardH;
    const overlapsDefault =
      cLeft < hRight && vw - MARGIN > hLeft && cTop < hBottom && vh - MARGIN > hTop;
    if (!overlapsDefault) {
      setAutoPos(null);
      return;
    }
    // 1) ハイライトの左隣へ横にずらす（下端は右下のまま）
    const shiftedRight = vw - hLeft + AVOID_GAP;
    if (vw - shiftedRight - CARD_W >= MARGIN) {
      setAutoPos({ right: shiftedRight, bottom: MARGIN });
      return;
    }
    // 2) 横に置けない場合はハイライトの上へ
    const shiftedBottom = vh - hTop + AVOID_GAP;
    if (vh - shiftedBottom - cardH >= MARGIN) {
      setAutoPos({ right: MARGIN, bottom: shiftedBottom });
      return;
    }
    // 3) それも無理なら右上へ退避（右上もかぶるなら左上）
    const topAnchorBottom = vh - MARGIN - cardH;
    const overlapsTopRight =
      cLeft < hRight &&
      vw - MARGIN > hLeft &&
      MARGIN < hBottom &&
      MARGIN + cardH > hTop;
    setAutoPos({
      right: overlapsTopRight ? vw - MARGIN - CARD_W : MARGIN,
      bottom: topAnchorBottom,
    });
  }, [rect, dragPos, vw, vh, stepIndex]);

  const paragraphs = body.split("\n").filter((p) => p.trim() !== "");

  const card = (
    <div
      ref={cardRef}
      style={{
        position: "fixed",
        width: CARD_W,
        maxWidth: "calc(100vw - 16px)",
        backgroundColor: cardBg,
        color: textColor,
        borderRadius: 12,
        boxShadow: "0 8px 30px rgba(0,0,0,0.35)",
        border: `1px solid ${borderColor}`,
        pointerEvents: "auto",
        overflow: "hidden",
        // ドラッグ中は追従を優先し、自動退避の移動のみ滑らかに動かす
        transition: dragPos ? "none" : "right 0.2s ease, bottom 0.2s ease",
        ...(dragPos
          ? { top: dragPos.top, left: dragPos.left }
          : (autoPos ?? { right: MARGIN, bottom: MARGIN })),
      }}
    >
      {/* ヘッダー（つかんでカードを移動できる） */}
      <div
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        style={{
          background: `linear-gradient(135deg, ${accent}, ${accent}CC)`,
          color: "#fff",
          padding: "10px 14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "move",
          touchAction: "none",
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            fontWeight: 700,
            fontSize: "0.95rem",
            minWidth: 0,
          }}
        >
          <DragIndicatorIcon style={{ fontSize: 18, opacity: 0.8 }} />
          <span
            style={{
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {title}
          </span>
        </span>
        <button
          type="button"
          onClick={onExit}
          aria-label="チュートリアルを終了"
          style={{
            display: "flex",
            alignItems: "center",
            color: "#fff",
            padding: 2,
            borderRadius: 9999,
          }}
        >
          <CloseIcon fontSize="small" />
        </button>
      </div>

      {/* 本文 */}
      <div style={{ padding: "12px 14px" }}>
        {paragraphs.map((p, i) => (
          <p
            key={i}
            style={{
              fontSize: "0.85rem",
              lineHeight: 1.7,
              marginBottom: i === paragraphs.length - 1 ? 0 : 8,
            }}
          >
            {p}
          </p>
        ))}
      </div>

      {/* フッター */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 14px",
          borderTop: `1px solid ${borderColor}`,
        }}
      >
        <span style={{ fontSize: "0.75rem", color: subColor }}>
          {stepIndex + 1} / {totalSteps}
        </span>
        {showNext ? (
          <button
            type="button"
            onClick={onNext}
            style={{
              backgroundColor: accent,
              color: "#fff",
              fontWeight: 700,
              fontSize: "0.85rem",
              padding: "6px 18px",
              borderRadius: 8,
              boxShadow: "0 2px 4px rgba(0,0,0,0.15)",
            }}
          >
            {isLast ? "完了" : "次へ"}
          </button>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span
              style={{
                fontSize: "0.75rem",
                color: accent,
                fontWeight: 700,
              }}
            >
              ハイライト部分を操作してください
            </span>
            {/* 操作せずに先へ進みたい人向け。押すと取るべき操作を代行して次へ進む */}
            <button
              type="button"
              onClick={onSkip}
              title="この操作を飛ばして次へ進む"
              style={{
                flexShrink: 0,
                border: `1px solid ${borderColor}`,
                color: subColor,
                fontWeight: 700,
                fontSize: "0.8rem",
                padding: "5px 12px",
                borderRadius: 8,
              }}
            >
              スキップ
            </button>
          </div>
        )}
      </div>
    </div>
  );

  // ===== 中央モーダル（ハイライトなし）=====
  if (!rect) {
    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 2147483646,
          pointerEvents: "none",
        }}
      >
        <div
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: dim,
            pointerEvents: "auto",
          }}
        />
        {card}
      </div>
    );
  }

  // ===== スポットライト（4 枚の暗転枠 + ハイライトリング）=====
  const top = Math.max(rect.top - PAD, 0);
  const left = Math.max(rect.left - PAD, 0);
  const w = rect.width + PAD * 2;
  const h = rect.height + PAD * 2;
  const frame: React.CSSProperties = {
    position: "fixed",
    backgroundColor: dim,
    pointerEvents: "auto",
  };

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 2147483646,
        pointerEvents: "none",
      }}
    >
      {/* 上 */}
      <div style={{ ...frame, top: 0, left: 0, width: "100vw", height: top }} />
      {/* 下 */}
      <div
        style={{
          ...frame,
          top: top + h,
          left: 0,
          width: "100vw",
          height: Math.max(vh - (top + h), 0),
        }}
      />
      {/* 左 */}
      <div style={{ ...frame, top, left: 0, width: left, height: h }} />
      {/* 右 */}
      <div
        style={{
          ...frame,
          top,
          left: left + w,
          width: Math.max(vw - (left + w), 0),
          height: h,
        }}
      />
      {/* ハイライトリング（クリックは透過） */}
      <div
        style={{
          position: "fixed",
          top,
          left,
          width: w,
          height: h,
          border: `2px solid ${accent}`,
          borderRadius: 10,
          boxShadow: `0 0 0 4px ${accent}55`,
          pointerEvents: "none",
        }}
      />
      {card}
    </div>
  );
};
